import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';

export type SkeletonVariant = 'text' | 'card' | 'stat' | 'table' | 'chart' | 'list';

export interface SkeletonLoaderProps {
  variant?: SkeletonVariant;
  /** Rows (table/list) or repeated blocks (card/stat). */
  count?: number;
  /** Columns, for the `table` variant. */
  columns?: number;
  height?: number;
}

/**
 * Layout-aware loading placeholders.
 *
 * Each variant mirrors the geometry of the component it replaces so the page
 * does not jump when real data arrives. The whole thing is `aria-hidden` with a
 * single polite live region announcing "loading", which is far less noisy for
 * screen-reader users than dozens of announced placeholder boxes.
 */
export function SkeletonLoader({
  variant = 'text',
  count = 3,
  columns = 5,
  height,
}: SkeletonLoaderProps): JSX.Element {
  const rows = Array.from({ length: count }, (_, index) => index);
  const cols = Array.from({ length: columns }, (_, index) => index);

  return (
    <>
      <Box role="status" aria-live="polite" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
        Caricamento in corso
      </Box>

      <Box aria-hidden="true">
        {variant === 'text' && (
          <Stack spacing={1}>
            {rows.map((row) => (
              <Skeleton key={row} variant="text" width={row === count - 1 ? '60%' : '100%'} />
            ))}
          </Stack>
        )}

        {variant === 'stat' && (
          <Box
            sx={{
              display: 'grid',
              gap: 4,
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: `repeat(${Math.min(count, 4)}, 1fr)` },
            }}
          >
            {rows.map((row) => (
              <Card key={row}>
                <CardContent sx={{ p: 5 }}>
                  <Skeleton variant="text" width="55%" height={16} />
                  <Skeleton variant="text" width="40%" height={40} sx={{ mt: 2 }} />
                  <Skeleton variant="text" width="70%" height={14} />
                </CardContent>
              </Card>
            ))}
          </Box>
        )}

        {variant === 'card' && (
          <Stack spacing={4}>
            {rows.map((row) => (
              <Card key={row}>
                <CardContent sx={{ p: 5 }}>
                  <Skeleton variant="text" width="35%" height={22} />
                  <Skeleton variant="rectangular" height={height ?? 96} sx={{ mt: 3, borderRadius: 1.5 }} />
                </CardContent>
              </Card>
            ))}
          </Stack>
        )}

        {variant === 'chart' && (
          <Stack spacing={3}>
            <Skeleton variant="text" width="30%" height={20} />
            <Skeleton variant="rectangular" height={height ?? 280} sx={{ borderRadius: 2 }} />
          </Stack>
        )}

        {variant === 'table' && (
          <Stack spacing={0} sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', gap: 4, px: 4, py: 3 }}>
              {cols.map((col) => (
                <Skeleton key={col} variant="text" height={14} sx={{ flex: col === 0 ? 2 : 1 }} />
              ))}
            </Box>
            {rows.map((row) => (
              <Box
                key={row}
                sx={{ display: 'flex', gap: 4, px: 4, py: 3, borderTop: '1px solid', borderColor: 'divider' }}
              >
                {cols.map((col) => (
                  <Skeleton key={col} variant="text" height={20} sx={{ flex: col === 0 ? 2 : 1 }} />
                ))}
              </Box>
            ))}
          </Stack>
        )}

        {variant === 'list' && (
          <Stack spacing={3}>
            {rows.map((row) => (
              <Box key={row} sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                <Skeleton variant="circular" width={36} height={36} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton variant="text" width="45%" height={16} />
                  <Skeleton variant="text" width="75%" height={14} />
                </Box>
              </Box>
            ))}
          </Stack>
        )}
      </Box>
    </>
  );
}
